import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  adoptZeovitRecommendation,
  deleteZeovitLineage,
  getZeovitAdoptPreview,
  getZeovitRecommendation,
  type ZeovitAdoptPreview,
  type ZeovitAdoptResult,
  type ZeovitRecommendation,
} from '../services/zeovitRecommendationService';

/**
 * Query keys of the ZeoVit recommendation caches. The list key is a prefix of every
 * single-recommendation key, so invalidating the list also refreshes cached details.
 */
export const zeovitRecommendationKeys = {
  list: (aquariumId: string | undefined) => ['zeovitRecommendations', aquariumId] as const,
  detail: (aquariumId: string | undefined, recommendationId: string | undefined) =>
    ['zeovitRecommendations', aquariumId, recommendationId] as const,
};

/** Cache key of one recommendation (same as `zeovitRecommendationKeys.detail`). */
export function zeovitRecommendationQueryKey(
  aquariumId: string | undefined,
  recommendationId: string | undefined,
): readonly ['zeovitRecommendations', string | undefined, string | undefined] {
  return zeovitRecommendationKeys.detail(aquariumId, recommendationId);
}

/** One ZeoVit dosing recommendation (GET …/zeovit-recommendations/:recommendationId). */
export function useZeovitRecommendation(
  aquariumId: string | undefined,
  recommendationId: string | undefined,
): UseQueryResult<ZeovitRecommendation, Error> {
  return useQuery({
    queryKey: zeovitRecommendationQueryKey(aquariumId, recommendationId),
    queryFn: () => getZeovitRecommendation(aquariumId as string, recommendationId as string),
    enabled: Boolean(aquariumId) && Boolean(recommendationId),
  });
}

/**
 * Adopts a pending recommendation. On success the cached recommendation is replaced
 * by the adopted one (status badge updates without a refetch) and the iteration list
 * is invalidated. On a 409 (no longer pending / schedule conflict) the recommendation
 * is refetched so the page shows its current status.
 */
export function useAdoptZeovitRecommendation(
  aquariumId: string | undefined,
  recommendationId: string | undefined,
): UseMutationResult<ZeovitAdoptResult, Error, readonly string[] | void> {
  const queryClient = useQueryClient();
  const detailKey = zeovitRecommendationQueryKey(aquariumId, recommendationId);

  return useMutation({
    // The planRefs the confirmation dialog listed as "will be ended" (adopt-preview).
    mutationFn: (confirmedEndPlanRefs: readonly string[] | void) =>
      adoptZeovitRecommendation(aquariumId as string, recommendationId as string, confirmedEndPlanRefs ?? []),
    onSuccess: (result) => {
      queryClient.setQueryData(detailKey, result.recommendation);
      // exact: only the list — the detail entry was just replaced and needs no refetch.
      void queryClient.invalidateQueries({ queryKey: zeovitRecommendationKeys.list(aquariumId), exact: true });
    },
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: detailKey });
    },
  });
}

/**
 * What adopting would end right now (GET …/adopt-preview): loaded when the adopt
 * confirmation dialog opens, always fresh (no cache reuse).
 */
export function useZeovitAdoptPreview(
  aquariumId: string | undefined,
  recommendationId: string | undefined,
  enabled: boolean,
): UseQueryResult<ZeovitAdoptPreview, Error> {
  return useQuery({
    queryKey: [...zeovitRecommendationQueryKey(aquariumId, recommendationId), 'adopt-preview'],
    queryFn: () => getZeovitAdoptPreview(aquariumId as string, recommendationId as string),
    enabled: enabled && Boolean(aquariumId) && Boolean(recommendationId),
    staleTime: 0,
    gcTime: 0,
  });
}

/**
 * Deletes the whole history (lineage) containing a recommendation. On success every
 * cached recommendation of the aquarium is invalidated (the list shows the next
 * current history or the empty state).
 */
export function useDeleteZeovitLineage(
  aquariumId: string | undefined,
): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (recommendationId: string) => deleteZeovitLineage(aquariumId as string, recommendationId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: zeovitRecommendationKeys.list(aquariumId) }),
  });
}
